import { Link } from '@design-system/Link';
import { Button } from '@design-system/Button';
import { FormulaCard } from '@design-system/Cards/FormulaCard';
import { SHOP_PATH, SMP_PATH } from '@/constants/paths';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import type { FoodProduct } from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';
import { getMyIngredientsPageUrl } from '@/utils/nutrition/getMyIngredientsPageUrl';
import { NutritionPageCommonSkeleton } from './NutritionPageCommonSkeleton';
import { useBootup } from '@/hooks/useBootup';
import { cn } from '@/utils/styles';
import { EventTracker } from '@/utils';

export interface NutritionPageFormulaSectionProps {
  isLoading?: boolean;
  formulas?: FoodProduct[];
  isError?: boolean;
  'data-testid'?: string;
}

const handleShopRedirection = () => {
  EventTracker.trackEvent(
    'nutrition',
    'nutrition_Formulas_gateway_order_formulas',
  );
  return SHOP_PATH;
};

const handleSMPRedirection = () => {
  EventTracker.trackEvent(
    'nutrition',
    'nutrition_Formulas_gateway_manage_my_subscription',
  );
  return SMP_PATH;
};

const handleIngredientRedirection = (name: string) => {
  EventTracker.trackEvent(
    'nutrition',
    `nutrition_Formulas_gateway_${name.replace(/\s+/g, '')}`,
  );
  return getMyIngredientsPageUrl({
    product: name,
  });
};

export const NutritionPageFormulaSection = ({
  formulas,
  isLoading,
  isError,
  'data-testid': testId,
}: NutritionPageFormulaSectionProps) => {
  const isFormulasSubscribed = formulas?.some((item) => item.is_subscribed);
  const { bootupState } = useBootup();

  if (isLoading) {
    return (
      <NutritionPageCommonSkeleton className="h-52 max-w-xl w-[10.5rem]" />
    );
  }

  if (isError || !formulas || formulas.length === 0) {
    return null;
  }

  return (
    <>
      <p className="mb-2 typography-h1 text-primary flex flex-row items-center gap-2" data-testid={testId}>
        {bootupState?.isEligibleForSupplements
          ? 'My Formulas'
          : 'My Supplements & Biotics'}
      </p>

      <p className="mb-6 typography-body text-tertiary lg:w-[35rem]" data-testid="nutrition-formulas-description">
        Accelerate your path to better health with supplements and biotics that
        fill nutrient gaps and target scores that need improvement.
      </p>

      <div
        className={cn(
          'w-full grid grid-cols-2 gap-2',
          'lg:grid lg:grid-cols-4 lg:gap-2',
          'lg:pb-4',
        )}
        data-testid="nutrition-formulas-grid"
      >
        {formulas?.map((product) => (
          <Link
            key={product.id}
            variant="flat"
            to={handleIngredientRedirection(product.name)}
            className="w-full"
            data-testid={`nutrition-formulas-card-${product.name.replace(/\s+/g, '-').toLowerCase()}`}
          >
            <FormulaCard
              title={product.name}
              formulaSrc={product.image_url}
              isSubscribed={product.is_subscribed}
              ingredientsCount={product.ingredients_count}
              isCustom={!bootupState?.isEligibleForSupplements}
              className="w-full h-full"
            />
          </Link>
        ))}
      </div>

      {bootupState?.isEligibleForSupplements &&
        (isFormulasSubscribed ? (
          <Button
            asChild
            variant="secondary"
            className="w-full h-12 [&_svg]:!size-6 justify-between pr-3 mt-6"
            data-testid="nutrition-formulas-manageSubscriptionButton"
          >
            <Link to={handleSMPRedirection()}>
              Manage my subscription
              <ChevronRightIcon height={24} width={24} />
            </Link>
          </Button>
        ) : (
          <Button
            asChild
            variant="outline"
            className="dark bg-overlay-black w-full h-12 [&_svg]:!size-6 justify-between pr-3 mt-6"
            data-testid="nutrition-formulas-orderButton"
          >
            <Link to={handleShopRedirection()}>
              Order my formulas
              <ChevronRightIcon height={24} width={24} />
            </Link>
          </Button>
        ))}
    </>
  );
};
